import { Injectable } from '@angular/core';
import { catchError, retry } from "rxjs/operators";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { Files, WebserviceResponse } from "../interfaces";
import { filesBaseUrl, uploadBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class FileStoreService {

  private readonly endpoint = filesBaseUrl();

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) {}

  private handleError(error: HttpErrorResponse){
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  public sendFile(
    fileToUpload: File,
    docType: number,
    subNumber: number,
    projectID: string,
    jobNumber: number,
    componentID: string,
    description: string,
    materialId?: number
  ) {
    const formData: FormData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);
    const materialQuery = materialId && materialId > 0 ? `&material_id=${materialId}` : '';
    return this.http.post(`${uploadBaseUrl()}/upload?doctype=${docType}&project=${projectID}&component=${componentID}&job_number=${jobNumber}&sub_number=${subNumber}&description=${description}${materialQuery}`
        ,formData).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getComponentsFilesByProject(projectID: string): Observable<WebserviceResponse<Files[]>>{
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        job_number: 0
      }
    });
    return this.http.get<WebserviceResponse<Files[]>>(this.endpoint + '/get_component_files_by_project_and_job_number',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getComponentsFilesByJob(projectID: string, jobID: number): Observable<WebserviceResponse<Files[]>>{
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        job_number: jobID
      }
    });
    return this.http.get<WebserviceResponse<Files[]>>(this.endpoint + '/get_component_files_by_project_and_job_number',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getComponentsFilesByOrder(projectID: string, orderID: number): Observable<WebserviceResponse<Files[]>>{
    const options = new HttpParams({
      fromObject: {
        project_number: projectID,
        order_id: orderID
      }
    });
    return this.http.get<WebserviceResponse<Files[]>>(this.endpoint + '/get_component_files_by_order',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getCompanyFiles(companyID: number): Observable<WebserviceResponse<Files[]>>{
    const options = new HttpParams({
      fromObject: {
        company_id: companyID.toString(),
        component: "0",
        doctype: [8,9,99],
        project: "0",
        sub_number: 0,
        job_number: 0
      }
    });
    return this.http.get<WebserviceResponse<Files[]>>(this.endpoint + '/get_files',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getFilesSet(docTypes: number[], projectID: string, jobID: number, subID: number): Observable<WebserviceResponse<Files[]>>{
    const doctypeParam = docTypes.join(',');
    const options = new HttpParams({
      fromObject: {
        component: "0",
        doctype: doctypeParam,
        project: projectID,
        sub_number: subID,
        job_number: jobID
      }
    });
    return this.http.get<WebserviceResponse<Files[]>>(this.endpoint + '/get_files',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public getFilesByIds(ids: string[]): Observable<WebserviceResponse<Files[]>> {
    const options = new HttpParams({
      fromObject: {
        ids: ids.join(',')
      }
    });

    return this.http.get<WebserviceResponse<Files[]>>(this.endpoint + '/get_files_by_ids',
      { params: options }
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public downloadFile(fileID: string){
    return this.http.get(
      `${this.endpoint}/download_file?file_id=${fileID}`,
      { responseType: 'blob'}
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public deleteFile(fileID: string){
    return this.http.delete(
      `${this.endpoint}/delete_file?file_id=${fileID}`,
      { responseType: 'text'}
    ).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }
}
